import React, { useRef, useEffect } from 'react';
import anychart from 'anychart';
import applyAnychartStyles from "./applyAnychartStyles";

/**
 * Grouped3DBarChart Component
 *
 * A component that renders a 3D grouped bar or column chart using AnyChart.
 *
 * @param {Array} data - The data array where each item is an object with an 'id' and value fields.
 *                       Example: [{ id: 'Category1', fieldName1: 10, fieldName2: 20 }, ...]
 * @param {Array} fieldNames - The list of field names to be used as the series for the chart.
 *                             Example: ['fieldName1', 'fieldName2']
 * @param {string} height - The height of the chart container (default is '400px').
 * @param {string} title - The title of the chart.
 * @param {Object} colors - An object mapping field names to colors. Example: { fieldName1: '#ff0000' }
 * @param {Object} opacities - An object mapping field names to opacity values. Example: { fieldName1: 0.7 }
 * @param {string} refreshKey - A key to force refresh the chart when changed.
 * @param {string} xTitle - The title of the x-axis.
 * @param {string} yTitle - The title of the y-axis.
 * @param {boolean} vertical - If true, display the chart as a column chart; if false, as a bar chart (default: false).
 */
const Grouped3DBarChart = ({
  data,
  fieldNames,
  height = '400px',
  title = '',
  colors = {},
  opacities,
  refreshKey,
  xTitle = '',
  yTitle = '',
  vertical = false
}) => {
  // Reference to the chart container div
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.dispose(); // Dispose the previous chart instance if it exists
    }

    // Create the chart instance based on the `vertical` prop
    const chart = vertical ? anychart.column3d() : anychart.bar3d();
    chartRef.current = chart;

    // Create the data set from the input data
    const dataSet = anychart.data.set(data);

    // Map the fields into data mappings for each field name provided
    const seriesData = fieldNames.map((fieldName) => {
      return dataSet.mapAs({ x: 'id', value: fieldName });
    });

    // Configure the chart settings
    chart.barGroupsPadding(1);

    // Add each series to the chart
    fieldNames.forEach((fieldName, index) => {

      const series = chart[vertical?'column':'bar'](seriesData[index]);
      series.name(fieldName);

      // Apply the color if it's provided in the colors prop
      if (colors[fieldName]) {
        const opacity = opacities?.[fieldName] || 1;
        series.fill(`${colors[fieldName]} ${opacity}`);
      }
      series.stroke('1 #b0c1cc 0.5');
      series.tooltip()
        .titleFormat('{%X}')
        .format(function () {
          // Rounding the value to 2 decimal places
          const roundedValue = Math.round(this.value * 100) / 100;
          return `${roundedValue}`;
        })
        .fontColor('#fff') // Set tooltip font color to white
        .background().fill('#000'); // Optional: Set background color for tooltip
    });

    // Set chart titles and interactivity options
    chart.title(title);

    // Adjust axis titles based on the `vertical` prop
    if (vertical) {
      chart.xAxis().title(xTitle); // x-axis represents categories
      chart.yAxis().title(yTitle); // y-axis represents values
    } else {
      chart.yAxis().title(xTitle); // For horizontal bars, the y-axis represents categories
      chart.xAxis().title(yTitle); // x-axis represents values
    }

    chart.zAspect('50%').zPadding(20).zAngle(65);

    // Enable the chart legend
    chart.legend(true);
    chart.legend().enabled(true).fontSize(13).padding([0, 0, 20, 0]);
    chart.yAxis().stroke('#fff');
    chart.xAxis().stroke('#fff');
    chart.yGrid().stroke('#fff');
    chart.xGrid().stroke('#fff');
      var xLabels = chart.xAxis().labels();



    xLabels.wordWrap("normal");
    xLabels.wordBreak("normal");
    xLabels.width(80);
    //
    applyAnychartStyles(chart);

    // Attach the chart to the container and draw it
    if (chartContainerRef.current) {
      chart.container(chartContainerRef.current);
      chart.draw();
    }

    // Clean up on unmount
    return () => {
      chart.dispose();
    };
  }, [data, fieldNames, colors, opacities, title, refreshKey, vertical, xTitle, yTitle]);

  // Handle resize events
  useEffect(() => {
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.container(chartContainerRef.current);
        chartRef.current.draw();
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial draw

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      ref={chartContainerRef}
      style={{
        width: '100%',
        height, // Use the provided height or default to 400px
      }}
    />
  );
};

export default Grouped3DBarChart;
